import { Trans, useLingui } from '@lingui/react/macro';
import { format } from 'date-fns';
import MediaSubjectPicker, { MediaSubjectItem } from 'modules/championship/ui/components/form-parts/media/media-subject-picker';
import MediaSubjectPickerSkeleton from 'modules/championship/ui/components/form-parts/media/media-subject-picker-skeleton';
import { MediaKind, mediaSubjectMatchDate } from 'modules/manager/domain/media-kind';
import { MediaSubjectType } from 'modules/manager/domain/media-kind/media-kind-catalog';
import { useChampionshipMediaSubjectsQuery } from 'modules/manager/store/media-generation-jobs-api';
import React, { useEffect, useMemo, useState } from 'react';
import UDButton from 'ud-ui/components/button';
import Modal from 'ud-ui/components/modal';
import { SkeletonPreview } from 'ud-ui/components/skeleton';
import UDText from 'ud-ui/components/text';

interface GenerateMediaModalProps {
  championshipId: number;
  kind: MediaKind;
  subjectType: MediaSubjectType;
  kindTitle: string;
  onClose: () => void;
  onSubmit: (subjectId: number) => void;
}

const GenerateMediaModal = ({ championshipId, subjectType, kindTitle, onClose, onSubmit }: GenerateMediaModalProps) => {
  const { t } = useLingui();
  const { data: subjects, isFetching } = useChampionshipMediaSubjectsQuery(championshipId);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const items: MediaSubjectItem[] = useMemo(() => {
    if (!subjects) return [];
    if (subjectType === 'match') {
      return subjects.matches.map((match) => {
        const home = match.teams[0]?.name ?? '—';
        const away = match.teams[1]?.name ?? '—';
        const date = mediaSubjectMatchDate(match.date);
        return {
          id: match.id,
          title: `${home} — ${away}`,
          group: match.round?.name,
          meta: date ? format(date, 'dd.MM.yyyy HH:mm') : undefined,
          // Анонс без даты печатался с «01.01.1970» (IMGM-1499): такой матч показываем, но не даём выбрать.
          disabledReason: date ? undefined : t`У матча не задана дата`,
        };
      });
    }
    if (subjectType === 'round') {
      return subjects.rounds.map((round) => ({ id: round.id, title: round.name }));
    }
    if (subjectType === 'stage') {
      return subjects.stages.map((stage) => ({ id: stage.id, title: stage.name }));
    }
    if (subjectType === 'group') {
      return subjects.groups.map((group) => ({ id: group.id, title: group.name }));
    }
    return [];
  }, [subjects, subjectType, t]);

  // Выбирать не из чего, кроме одного варианта, — выбираем его сразу.
  useEffect(() => {
    const available = items.filter((item) => !item.disabledReason);
    if (selectedId === null && available.length === 1) setSelectedId(available[0].id);
  }, [items, selectedId]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (selectedId === null) return;
    onSubmit(selectedId);
  };

  return (
    // Выбор — списком прямо в теле окна, а не выпадашкой: выпадающее меню внутри диалога
    // обрезалось им или перекрывало кнопки (IMGM-1508). У списка своя высота и скролл,
    // заголовок и кнопки окна видны всегда. Окно — форма: Enter на выбранной радиокнопке
    // отправляет её штатно, как кнопка «Сгенерировать».
    <Modal show onClose={onClose} size="md">
      <Modal.Header title={t`Сгенерировать: ${kindTitle}`} />
      <form onSubmit={handleSubmit} noValidate aria-label={t`Сгенерировать: ${kindTitle}`}>
        <Modal.Body>
          <div data-testid="generate-media-modal">
            {/* Страховка на клик раньше ответа: объекты грузит подвкладка «Шаблоны» заранее, и обычно окно
                открывается сразу со списком (IMGM-1514). Скелетон — по типу объекта (IMGM-1508). */}
            {isFetching && !subjects ? (
              <MediaSubjectPickerSkeleton subjectType={subjectType} />
            ) : items.length === 0 ? (
              <UDText type="subhead" className="text-inkSecondary">
                <Trans>Нет подходящих объектов для этого типа медиа.</Trans>
              </UDText>
            ) : (
              <SkeletonPreview skeleton={<MediaSubjectPickerSkeleton subjectType={subjectType} />}>
                <MediaSubjectPicker items={items} value={selectedId} onChange={setSelectedId} label={t`Для чего сгенерировать`} />
              </SkeletonPreview>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <UDButton variant="secondary" type="button" onClick={onClose}>
            <Trans>Отмена</Trans>
          </UDButton>
          <UDButton variant="primary" type="submit" loading={false} disabled={selectedId === null} data-testid="generate-media-submit">
            <Trans>Сгенерировать</Trans>
          </UDButton>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default GenerateMediaModal;
